import type { MoneyString } from '@/lib/money';

/** Taxa em DECIMAL (0.0199 = 1,99% a.m.), como o backend grava. */
export type RateString = string;

export type Loan = {
  id: number;
  description: string;
  type: string;
  entity_id: number;
  entity_name: string | null;
  creditor_name: string | null;
  /** RF-PAG-03 — entra na priorização quando o caixa aperta. */
  is_essential: boolean;
  contract_number: string | null;

  contracted_amount: MoneyString;
  net_released_amount: MoneyString;
  financed_amount: MoneyString;
  current_balance: MoneyString;
  iof_amount: MoneyString;
  fees_amount: MoneyString;

  interest_rate_monthly: RateString;
  interest_rate_annual: RateString | null;
  /** RN-68 — a única taxa que torna propostas comparáveis. */
  cet_monthly: RateString | null;
  cet_annual: RateString | null;

  amortization_system: string;
  installments_count: number;
  installment_amount: MoneyString | null;
  grace_months: number;
  grace_type: string | null;

  contracted_at: string | null;
  first_due_date: string | null;
  last_due_date: string | null;
  next_due_date: string | null;

  status: string;
  total_paid: MoneyString;
  total_principal_paid: MoneyString;
  total_interest_paid: MoneyString;
  settled_at: string | null;
  account_id: number | null;
};

export type Installment = {
  id: number;
  number: number;
  due_date: string;
  total: MoneyString;
  /** RN-61: reduz o passivo, nunca entra no resultado. */
  principal: MoneyString;
  /** RN-61: despesa financeira de verdade. */
  interest: MoneyString;
  insurance: MoneyString;
  fees: MoneyString;
  expense_share: MoneyString;
  balance_after: MoneyString;
  is_grace_period: boolean;
  status: string;
  paid_at: string | null;
  remaining: MoneyString;
  is_overdue: boolean;
};

export type LoanDetail = Loan & {
  installments: Installment[];
  /** Divergência entre o saldo gravado e o derivado do cronograma. */
  balance_drift: MoneyString;
};

export type DebtSummary = {
  total_debt: MoneyString;
  active_count: number;
  interest_paid_total: MoneyString;
  /** O que ainda vira despesa financeira se nada mudar. */
  interest_ahead: MoneyString;
  overdue_count: number;
  highest_rate_monthly: RateString | null;
};

export type ProposalResult = {
  index: number;
  label: string;
  requested_amount: MoneyString;
  net_released_amount: MoneyString;
  iof_amount: MoneyString;
  fees_amount: MoneyString;
  interest_rate_monthly: RateString;
  installments_count: number;
  grace_months: number;
  amortization_system: string;
  installment_amount: MoneyString | null;
  schedule_length: number;
  total_paid: MoneyString;
  total_interest: MoneyString;
  total_cost: MoneyString;
  cet_monthly: RateString;
  cet_annual: RateString;
  present_value: MoneyString;
  is_baseline: boolean;
  is_recommended: boolean;
  installment_delta: MoneyString | null;
  total_paid_delta: MoneyString | null;
  present_value_delta: MoneyString | null;
  /** RN-70 — o aviso anti-armadilha. */
  warning: string | null;
  verdict: string;
};

export type ComparisonResult = {
  discount_rate_monthly: RateString;
  first_due_date: string;
  proposals: ProposalResult[];
  recommended_index: number;
  criterion: string;
  disclaimer: string;
};

export type PayoffQuote = {
  installments_remaining: number;
  nominal_sum: MoneyString;
  payoff_amount: MoneyString;
  interest_waived: MoneyString;
  outstanding_principal: MoneyString;
  future_interest_if_kept: MoneyString;
  months_saved: number;
  note: string;
};

/** Taxa decimal para percentual legível: 0.0199 → "1,99%". */
export function formatRate(rate: RateString | null | undefined, digits = 2): string {
  if (rate == null || rate === '') return '—';
  return `${(Number(rate) * 100).toFixed(digits).replace('.', ',')}%`;
}
